import type { Evidence } from '@packages/types';
import { useGetRawDocumentContentQuery } from '@ui/app/api';
import { Alert, Card, Spin } from 'antd';
import DocxViewer from './DocxViewer';
import PdfHighligter from './PdfHighligter';
import TxtViewer from './TxtViewer';

interface DocumentContentViewerProps {
    sourceFile: Evidence;
    allSupportingEvidence?: Evidence[];
    allContradictoryEvidence?: Evidence[];
}

export default function DocumentContentViewer({
    sourceFile,
    allSupportingEvidence = [],
    allContradictoryEvidence = [],
}: DocumentContentViewerProps) {
    const {
        data: documentData,
        isLoading,
        error,
    } = useGetRawDocumentContentQuery({ documentId: sourceFile.source_file || '' }, { skip: !sourceFile.source_file });

    const snippet = sourceFile.snippet;

    // Get all snippets from the current source file for highlighting
    const supportingSnippets =
        allSupportingEvidence
            ?.filter(ev => ev.source_file === sourceFile.source_file)
            .map(ev => ev.snippet)
            .filter(Boolean) || [];

    const contradictorySnippets =
        allContradictoryEvidence
            ?.filter(ev => ev.source_file === sourceFile.source_file)
            .map(ev => ev.snippet)
            .filter(Boolean) || [];

    if (isLoading) {
        return (
            <Card>
                <div style={{ textAlign: 'center', padding: 24 }}>
                    <Spin
                        size="large"
                        tip="Loading document..."
                    />
                </div>
            </Card>
        );
    }

    if (error) {
        return (
            <Alert
                type="warning"
                message="Document Not Found"
                description={
                    // @ts-expect-error - Error type is generic
                    error?.data?.message || error?.message || 'Failed to load document from server'
                }
                showIcon
            />
        );
    }

    const textContent = documentData?.content;
    const ext: string = 'txt';

    if (!textContent) {
        return (
            <Alert
                type="info"
                message="No Document"
                description="Document content is not available"
                showIcon
            />
        );
    }

    return (
        <Card
            size="small"
            style={{ maxHeight: '600px', overflow: 'auto' }}
        >
            {ext === 'txt' && (
                <TxtViewer
                    loading={false}
                    highlightKeywords={snippet ? [snippet] : []}
                    supportingKeywords={supportingSnippets}
                    contradictoryKeywords={contradictorySnippets}
                    consolidationResult={undefined}
                    textContent={textContent}
                />
            )}

            {ext === 'pdf' && (
                <PdfHighligter
                    fileUrl={textContent}
                    highlightedText={snippet}
                />
            )}

            {ext === 'docx' && (
                <DocxViewer
                    fileUrl={textContent}
                    highlightKeywords={snippet ? [snippet] : []}
                />
            )}
        </Card>
    );
}
